import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRoute, useNavigation, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import theme from '../../theme';
import { 
  getAssignmentHistory, 
  getDriverById, 
  getVehicleById
} from '../../api/supabase';
import { AssignmentHistory, Driver, Vehicle } from '../../types';
import { ReceivingHistoryDisplay } from '../../components/forms/ReceivingHistoryDisplay';
import { DeliveryHistoryDisplay } from '../../components/forms/DeliveryHistoryDisplay';

type AssignmentDetailsStackParamList = {
  AssignmentDetails: { assignmentId: string };
};

type AssignmentDetailsScreenRouteProp = RouteProp<AssignmentDetailsStackParamList, 'AssignmentDetails'>;
type AssignmentDetailsScreenNavigationProp = StackNavigationProp<AssignmentDetailsStackParamList>;

interface Props {
  route?: AssignmentDetailsScreenRouteProp;
  navigation?: AssignmentDetailsScreenNavigationProp;
}

type TabType = 'assignment' | 'receiving' | 'delivery' | 'driverAForms' | 'driverBForms';
type FormSubTabType = 'delivery' | 'receiving'; // ✅ NEW: Sub-tabs for partnership forms

export const AssignmentDetailsScreen: React.FC<Props> = (props) => {
  // Use hooks to get navigation props if not provided (for React Navigation auto-injection)
  const routeHook = useRoute<AssignmentDetailsScreenRouteProp>();
  const navigationHook = useNavigation<AssignmentDetailsScreenNavigationProp>();
  
  const route = props.route || routeHook;
  const navigation = props.navigation || navigationHook;
  
  const { assignmentId } = route.params;
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState<AssignmentHistory | null>(null);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [secondaryDriver, setSecondaryDriver] = useState<Driver | null>(null);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('assignment');
  const [activeFormSubTab, setActiveFormSubTab] = useState<FormSubTabType>('delivery'); // ✅ NEW: Sub-tab state
  const [error, setError] = useState<string | null>(null);

  const screenWidth = Dimensions.get('window').width;
  const isWeb = Platform.OS === 'web';
  const isMobile = Platform.OS !== 'web' || screenWidth < 768;

  useEffect(() => {
    loadAssignmentDetails();
  }, [assignmentId]);

  const loadAssignmentDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all assignment history and find the specific one
      const allAssignments = await getAssignmentHistory();
      const foundAssignment = allAssignments.find(a => a.id === assignmentId);

      if (!foundAssignment) {
        throw new Error('Assignment not found');
      }

      setAssignment(foundAssignment);

      // Load related data
      if (foundAssignment.primary_driver_id) {
        try {
          const driverData = await getDriverById(foundAssignment.primary_driver_id);
          setDriver(driverData);
        } catch (driverError) {
          console.warn('Could not load driver data:', driverError);
        }
      }

      // Load secondary driver for shared assignments
      if (foundAssignment.is_shared && foundAssignment.secondary_driver_id) {
        try {
          const secondaryDriverData = await getDriverById(foundAssignment.secondary_driver_id);
          setSecondaryDriver(secondaryDriverData);
        } catch (secondaryDriverError) {
          console.warn('Could not load secondary driver data:', secondaryDriverError);
        }
      }

      if (foundAssignment.vehicle_id) {
        try {
          const vehicleData = await getVehicleById(foundAssignment.vehicle_id);
          setVehicle(vehicleData);
        } catch (vehicleError) {
          console.warn('Could not load vehicle data:', vehicleError);
        }
      }
    } catch (error) {
      console.error('Error loading assignment details:', error);
      setError(error instanceof Error ? error.message : 'Failed to load assignment details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `AED ${amount.toFixed(2)}`;
  };

  const calculateDuration = () => {
    if (!assignment?.assignment_date) return 'N/A';
    
    const startDate = new Date(assignment.assignment_date);
    const endDate = assignment.return_date ? new Date(assignment.return_date) : new Date();
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  const renderTabButton = (tab: TabType, title: string, icon: string) => (
    <TouchableOpacity
      style={[
        styles.tabButton,
        activeTab === tab && styles.activeTabButton
      ]}
      onPress={() => setActiveTab(tab)}
    >
      <Ionicons 
        name={icon as any} 
        size={18} 
        color={activeTab === tab ? theme.colors.white : theme.colors.gray600} 
      />
      <Text style={[
        styles.tabButtonText,
        activeTab === tab && styles.activeTabButtonText
      ]}>
        {title}
      </Text>
    </TouchableOpacity>
  );

  // ✅ NEW: Render sub-tabs for partnership forms (Delivery vs Receiving)
  const renderFormSubTabButton = (subTab: FormSubTabType, title: string, icon: string) => (
    <TouchableOpacity
      style={[
        styles.formSubTabButton,
        activeFormSubTab === subTab && styles.activeFormSubTabButton
      ]}
      onPress={() => setActiveFormSubTab(subTab)}
    >
      <Ionicons 
        name={icon as any} 
        size={16} 
        color={activeFormSubTab === subTab ? theme.colors.primary : theme.colors.gray600} 
      />
      <Text style={[
        styles.formSubTabButtonText,
        activeFormSubTab === subTab && styles.activeFormSubTabButtonText
      ]}>
        {title}
      </Text>
    </TouchableOpacity>
  );

  const renderAssignmentTab = () => (
    <ScrollView style={styles.tabContent}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Assignment Information</Text>
        
        <View style={styles.infoGrid}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Assignment ID:</Text>
            <Text style={styles.infoValue}>{assignment?.id}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Assignment Date:</Text>
            <Text style={styles.infoValue}>{formatDate(assignment?.assignment_date)}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Return Date:</Text>
            <Text style={styles.infoValue}>{formatDate(assignment?.return_date)}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Duration:</Text>
            <Text style={styles.infoValue}>{calculateDuration()}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Assignment Type:</Text>
            <Text style={styles.infoValue}>{assignment?.assignment_type || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Status:</Text>
            <View style={[
              styles.statusBadge,
              { backgroundColor: assignment?.status === 'active' ? theme.colors.success : theme.colors.warning }
            ]}>
              <Text style={styles.statusText}>{assignment?.status || 'N/A'}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Vehicle Details</Text>
        <View style={styles.infoGrid}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>License Plate:</Text>
            <Text style={styles.infoValue}>{vehicle?.licensePlate || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Make & Model:</Text>
            <Text style={styles.infoValue}>{vehicle?.makeModel || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Year:</Text>
            <Text style={styles.infoValue}>{vehicle?.year || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>VIN:</Text>
            <Text style={styles.infoValue}>{vehicle?.vinNumber || 'N/A'}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <View style={styles.sectionHeaderWithIndicator}>
          <Text style={styles.sectionTitle}>Driver Details</Text>
          {assignment?.is_shared && (
            <View style={styles.sharedIndicator}>
              <Ionicons name="people" size={16} color={theme.colors.primary} />
              <Text style={styles.sharedIndicatorText}>Partner Assignment</Text>
            </View>
          )}
        </View>
        <View style={styles.infoGrid}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Primary Driver:</Text>
            <Text style={styles.infoValue}>{driver?.name || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Phone:</Text>
            <Text style={styles.infoValue}>{driver?.phone || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>License Number:</Text>
            <Text style={styles.infoValue}>{driver?.licenseNumber || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Nationality:</Text>
            <Text style={styles.infoValue}>{driver?.nationality || 'N/A'}</Text>
          </View>

          {/* Secondary Driver Information */}
          {assignment?.is_shared && secondaryDriver && (
            <>
              <View style={styles.divider} />
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Secondary Driver (Partner):</Text>
                <Text style={styles.infoValue}>{secondaryDriver.name}</Text>
              </View>
              
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Partner Phone:</Text>
                <Text style={styles.infoValue}>{secondaryDriver.phone || 'N/A'}</Text>
              </View>
              
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Partner License:</Text>
                <Text style={styles.infoValue}>{secondaryDriver.licenseNumber || 'N/A'}</Text>
              </View>
              
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Partner Nationality:</Text>
                <Text style={styles.infoValue}>{secondaryDriver.nationality || 'N/A'}</Text>
              </View>
            </>
          )}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Financial Details</Text>
        <View style={styles.infoGrid}>
          {assignment?.monthly_rent_rate && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Monthly Rate:</Text>
              <Text style={[styles.infoValue, styles.primaryRate]}>{formatCurrency(assignment.monthly_rent_rate)}/month</Text>
            </View>
          )}
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Daily Rate:</Text>
            <Text style={styles.infoValue}>
              {formatCurrency(assignment?.daily_rate || (assignment?.monthly_rent_rate ? assignment.monthly_rent_rate / 30 : 0))}
              {assignment?.monthly_rent_rate && ' (calculated)'}
            </Text>
          </View>
          
          {assignment?.is_shared && assignment?.monthly_rent_rate && (
            <View style={styles.rateBreakdownBox}>
              <Text style={styles.breakdownTitle}>Partnership Rate Split (50/50):</Text>
              <View style={styles.breakdownRow}>
                <Text style={styles.breakdownLabel}>• Each driver (monthly):</Text>
                <Text style={styles.breakdownValue}>{formatCurrency(assignment.monthly_rent_rate / 2)}</Text>
              </View>
              <View style={styles.breakdownRow}>
                <Text style={styles.breakdownLabel}>• Each driver (daily):</Text>
                <Text style={styles.breakdownValue}>{formatCurrency(assignment.daily_rate / 2)}</Text>
              </View>
            </View>
          )}
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Total Days:</Text>
            <Text style={styles.infoValue}>{assignment?.total_days || 'N/A'}</Text>
          </View>
          
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Total Amount:</Text>
            <Text style={styles.infoValue}>{formatCurrency(assignment?.total_amount)}</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderReceivingTab = () => {
    return (
      <ReceivingHistoryDisplay
        assignmentId={assignmentId}
        vehicle={vehicle || undefined}
        driver={driver || undefined}
      />
    );
  };

  const renderDeliveryTab = () => {
    return (
      <DeliveryHistoryDisplay
        assignmentId={assignmentId}
        vehicle={vehicle || undefined}
        driver={driver || undefined}
      />
    );
  };

  // Partnership - Driver A Forms Tab (Primary Driver)
  const renderDriverAFormsTab = () => {
    return (
      <View style={styles.tabContent}>
        {/* Header showing driver info */}
        <View style={styles.partnershipFormsHeader}>
          <Ionicons name="person" size={24} color={theme.colors.primary} />
          <Text style={styles.partnershipFormsTitle}>
            {driver?.name || 'Primary Driver'} - Forms
          </Text>
        </View>

        {/* ✅ NEW: Sub-tabs for Delivery vs Receiving */}
        <View style={styles.formSubTabContainer}>
          {renderFormSubTabButton('delivery', 'Delivery Form', 'car-outline')}
          {assignment?.status !== 'active' && renderFormSubTabButton('receiving', 'Receiving Form', 'clipboard-outline')}
        </View>

        {/* Conditionally render form based on active sub-tab */}
        <ScrollView style={styles.formContentContainer}>
          {activeFormSubTab === 'delivery' ? (
            <DeliveryHistoryDisplay
              assignmentId={assignmentId}
              vehicle={vehicle || undefined}
              driver={driver || undefined}
              suffix="A"
            />
          ) : (
            assignment?.status !== 'active' && (
              <ReceivingHistoryDisplay
                assignmentId={assignmentId}
                vehicle={vehicle || undefined}
                driver={driver || undefined}
                suffix="A"
              />
            )
          )}
        </ScrollView>
      </View>
    );
  };

  // Partnership - Driver B Forms Tab (Secondary Driver)
  const renderDriverBFormsTab = () => {
    return (
      <View style={styles.tabContent}>
        {/* Header showing driver info */}
        <View style={styles.partnershipFormsHeader}>
          <Ionicons name="people" size={24} color={theme.colors.success} />
          <Text style={styles.partnershipFormsTitle}>
            {secondaryDriver?.name || 'Partner Driver'} - Forms
          </Text>
        </View>

        {/* ✅ NEW: Sub-tabs for Delivery vs Receiving */}
        <View style={styles.formSubTabContainer}>
          {renderFormSubTabButton('delivery', 'Delivery Form', 'car-outline')}
          {assignment?.status !== 'active' && renderFormSubTabButton('receiving', 'Receiving Form', 'clipboard-outline')}
        </View>

        {/* Conditionally render form based on active sub-tab */}
        <ScrollView style={styles.formContentContainer}>
          {activeFormSubTab === 'delivery' ? (
            <DeliveryHistoryDisplay
              assignmentId={assignmentId}
              vehicle={vehicle || undefined}
              driver={secondaryDriver || undefined}
              suffix="B"
            />
          ) : (
            assignment?.status !== 'active' && (
              <ReceivingHistoryDisplay
                assignmentId={assignmentId}
                vehicle={vehicle || undefined}
                driver={secondaryDriver || undefined}
                suffix="B"
              />
            )
          )}
        </ScrollView>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading assignment details...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="alert-circle-outline" size={64} color={theme.colors.error} />
        <Text style={styles.errorTitle}>Error Loading Assignment</Text>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={loadAssignmentDetails}>
          <Text style={styles.retryButtonText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!assignment) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="document-outline" size={64} color={theme.colors.gray400} />
        <Text style={styles.errorTitle}>Assignment Not Found</Text>
        <Text style={styles.errorText}>The requested assignment could not be found.</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>Assignment Details</Text>
          <Text style={styles.headerSubtitle}>
            {vehicle?.licensePlate} - {driver?.name}
          </Text>
        </View>
      </View>

      {/* Tabs - Conditional based on assignment status and partnership */}
      <View style={styles.tabContainer}>
        {renderTabButton('assignment', 'Assignment', 'information-circle-outline')}
        
        {/* Partnership assignments: Show separate tabs for each driver's forms */}
        {assignment?.is_shared ? (
          <>
            {renderTabButton('driverAForms', `${driver?.name || 'Driver A'} Forms`, 'person-outline')}
            {renderTabButton('driverBForms', `${secondaryDriver?.name || 'Driver B'} Forms`, 'people-outline')}
          </>
        ) : (
          <>
            {/* Regular assignment: Show combined delivery/receiving tabs */}
            {renderTabButton('delivery', 'Delivery Form', 'car-outline')}
            {assignment?.status !== 'active' && renderTabButton('receiving', 'Receiving Form', 'clipboard-outline')}
          </>
        )}
      </View>

      {/* Tab Content */}
      <View style={styles.contentContainer}>
        {activeTab === 'assignment' && renderAssignmentTab()}
        
        {/* Partnership forms tabs */}
        {activeTab === 'driverAForms' && renderDriverAFormsTab()}
        {activeTab === 'driverBForms' && renderDriverBFormsTab()}
        
        {/* Regular assignment forms tabs */}
        {activeTab === 'delivery' && !assignment?.is_shared && renderDeliveryTab()}
        {activeTab === 'receiving' && !assignment?.is_shared && assignment?.status !== 'active' && renderReceivingTab()}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.gray100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  tabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.sm,
    gap: 8,
  },
  activeTabButton: {
    backgroundColor: theme.colors.primary,
  },
  tabButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.gray600,
  },
  activeTabButtonText: {
    color: theme.colors.white,
  },
  contentContainer: {
    flex: 1,
  },
  tabContent: {
    flex: 1,
    padding: theme.spacing.md,
  },
  section: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  infoGrid: {
    gap: theme.spacing.sm,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  infoLabel: {
    fontSize: 14,
    color: theme.colors.gray600,
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
    flex: 1,
    textAlign: 'right',
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.white,
    textTransform: 'uppercase',
  },
  placeholderContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
  },
  placeholderTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
    textAlign: 'center',
  },
  placeholderText: {
    fontSize: 16,
    color: theme.colors.gray600,
    marginTop: theme.spacing.sm,
    textAlign: 'center',
  },
  placeholderSubtext: {
    fontSize: 14,
    color: theme.colors.gray500,
    marginTop: theme.spacing.sm,
    textAlign: 'center',
    lineHeight: 20,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
  },
  loadingText: {
    fontSize: 16,
    color: theme.colors.gray600,
    marginTop: theme.spacing.md,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.xl,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.md,
    textAlign: 'center',
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.gray600,
    marginTop: theme.spacing.sm,
    textAlign: 'center',
  },
  retryButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: theme.spacing.md,
  },
  retryButtonText: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  successText: {
    color: theme.colors.success,
  },
  warningText: {
    color: theme.colors.warning,
  },
  dangerText: {
    color: theme.colors.error,
  },
  notesText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    lineHeight: 20,
  },
  damagesList: {
    marginTop: theme.spacing.sm,
  },
  damageItem: {
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.sm,
    borderRadius: 6,
    marginBottom: theme.spacing.xs,
  },
  damageType: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.error,
  },
  damageLocation: {
    fontSize: 12,
    color: theme.colors.gray600,
    marginTop: 2,
  },
  damageDescription: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    marginTop: 4,
  },
  // Shared Assignment Styles
  sectionHeaderWithIndicator: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sharedIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  sharedIndicatorText: {
    fontSize: 12,
    color: theme.colors.primary,
    fontWeight: '600',
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.gray200,
    marginVertical: theme.spacing.md,
    marginHorizontal: -theme.spacing.md,
  },
  primaryRate: {
    fontWeight: '600',
    color: theme.colors.primary,
  },
  rateBreakdownBox: {
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.md,
    borderRadius: 8,
    marginTop: theme.spacing.sm,
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.primary,
  },
  breakdownTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
  },
  breakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  breakdownLabel: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  breakdownValue: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  partnershipFormsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: 8,
    marginBottom: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  partnershipFormsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  // ✅ NEW: Form sub-tabs styles
  formSubTabContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    padding: 4,
    marginBottom: theme.spacing.md,
    gap: 8,
  },
  formSubTabButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    gap: 6,
    backgroundColor: theme.colors.gray50,
  },
  activeFormSubTabButton: {
    backgroundColor: theme.colors.primary + '15',
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  formSubTabButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: theme.colors.gray600,
  },
  activeFormSubTabButtonText: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  formContentContainer: {
    flex: 1,
  },
  formSection: {
    marginBottom: theme.spacing.lg,
  },
  formSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
  },
});

export default AssignmentDetailsScreen;